'use client';

import { Button } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import { AppDownloadLinks } from '../../../AppDownloadLinks/AppDownloadLinks';
import { useOfferTimer } from '../../hooks/useOfferTimer';
import type { BundleAssignationFailureReason } from '../../types';
import { getOfferContinueButtonTrackingAttributes } from '../../utils/offerUtils';
import { BrowseGamesButton } from '../BrowseGamesButton/BrowseGamesButton';
import { OfferLoginButtons } from '../OfferLoginButtons/OfferLoginButtons';
import { OfferNotEligible } from '../OfferNotEligible/OfferNotEligible';
import { OfferStickyPanel } from '../OfferStickyPanel/OfferStickyPanel';

type OfferStickyPanelButtonsProps = {
  shouldShowLoginButtons: boolean;
  offerValidFrom?: Date;
  onStepAction?: () => void;
  buttonLoading: string | null;
  currentAction: string | null;
  currentButtonText: string | null;
  currentButtonDisabled?: boolean;
  currentStep?: string;
  stepCode?: string;
  errorCode?: BundleAssignationFailureReason;
  gamesNames?: string[];
  recurrenceNextAvailableDate?: string;
};

function OfferStickyPanelButtons({
  shouldShowLoginButtons,
  offerValidFrom,
  onStepAction,
  buttonLoading,
  currentAction,
  currentButtonText,
  currentButtonDisabled = false,
  currentStep,
  stepCode,
  errorCode,
  gamesNames,
  recurrenceNextAvailableDate,
}: OfferStickyPanelButtonsProps) {
  const t = useTranslations('offers.page.obs.offer.cta');
  const { getTimerButtonText, isTimerDisabled } = useOfferTimer(offerValidFrom);

  const recurrenceClaimAgainText =
    errorCode === 'RECURRENCE_NOT_ASSIGNABLE' && recurrenceNextAvailableDate
      ? t('claim_againby', {
          date: new Date(recurrenceNextAvailableDate),
        })
      : null;

  const getButtonText = (originalText: string, action: string) => {
    if (buttonLoading === action.toLowerCase()) {
      return 'Loading...';
    }
    return getTimerButtonText(originalText);
  };

  if (errorCode) {
    return (
      <OfferStickyPanel className="p-5 md:p-0">
        {recurrenceClaimAgainText ? (
          <Button variant="primary" disabled className="mb-5" data-testid="sticky-panel-button-claim-again">
            {recurrenceClaimAgainText}
          </Button>
        ) : null}
        <OfferNotEligible errorCode={errorCode} />
        {errorCode === 'BUNDLE_LOCKED_BY_PLATFORM' ? <AppDownloadLinks className="mt-5 justify-center" /> : null}
      </OfferStickyPanel>
    );
  }

  if (shouldShowLoginButtons) {
    return (
      <OfferStickyPanel>
        <OfferLoginButtons offerValidFrom={offerValidFrom} buttonLoading={buttonLoading} />
      </OfferStickyPanel>
    );
  }

  return (
    <OfferStickyPanel>
      {currentAction && currentButtonText && (
        <>
          {gamesNames?.length ? (
            <BrowseGamesButton gamesNames={gamesNames} />
          ) : (
            <Button
              variant="primary"
              onClick={() => onStepAction?.()}
              disabled={buttonLoading !== null || currentButtonDisabled || isTimerDisabled()}
              data-testid={`sticky-panel-button-${currentAction}`}
              {...getOfferContinueButtonTrackingAttributes(currentStep, stepCode, currentAction)}
            >
              {getButtonText(currentButtonText, currentAction.toLowerCase())}
            </Button>
          )}
        </>
      )}
    </OfferStickyPanel>
  );
}

export { OfferStickyPanelButtons, type OfferStickyPanelButtonsProps };
